//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import React, { Component } from "react";
import { Form, Grid, Message } from "semantic-ui-react";
import { loadStripe, Stripe, StripeElements, StripeCardElement } from "@stripe/stripe-js";
import { Elements, CardElement, ElementsConsumer } from "@stripe/react-stripe-js";
import { PreparedPaymentMethod as ApiPreparedPaymentMethod, CreatePaymentMethodRequest as ApiCreatePaymentMethodRequest } from "../../api/lib";

// Interface describing the properties of the Stripe credit card component
interface IStripeCreditCardProps {
  preparedPaymentMethod: ApiPreparedPaymentMethod;
  onCreate: (req: ApiCreatePaymentMethodRequest) => void;
  onCanCreate: (canCreate: boolean) => void;
  onSaveCanceled: () => void;
}

// Interface describing the state of the Stripe credit card component
interface IStripeCreditCardState {
  errorMessage?: string;
  cardComplete: boolean;
  preparedPaymentMethod: ApiPreparedPaymentMethod;
  stripePromise: Promise<Stripe | null> | null;
}

// Identity of a prepared payment method for the Stripe form. A change in any of these means
// the form must be reset and Stripe re-initialized: token is the client secret, and
// script_url holds the Stripe publishable key (a key-only change must still reload Stripe).
const ppmIdentityChanged = (a: ApiPreparedPaymentMethod, b: ApiPreparedPaymentMethod) =>
  a.provider_id !== b.provider_id || a.token !== b.token || a.script_url !== b.script_url;

// Inner form that has access to stripe and elements via ElementsConsumer
interface IStripeCardFormProps {
  stripe: Stripe | null;
  elements: StripeElements | null;
  onCanCreate: (canCreate: boolean) => void;
  onSaveCanceled: () => void;
  errorMessage?: string;
  onCardChange: (complete: boolean, error?: string) => void;
  innerRef: (ref: { onClickSave: () => Promise<void> }) => void;
  clientSecret: string;
  onCreate: (req: ApiCreatePaymentMethodRequest) => void;
  preparedPaymentMethod: ApiPreparedPaymentMethod;
}

class StripeCardForm extends Component<IStripeCardFormProps> {
  componentDidMount() {
    this.props.innerRef({
      onClickSave: this.onClickSave,
    });
  }

  onClickSave = async () => {
    const { stripe, elements, clientSecret } = this.props;
    try {
      if (!stripe || !elements) {
        throw new Error("Stripe is not ready yet, please try again.");
      }
      const cardElement = elements.getElement(CardElement) as StripeCardElement;
      if (!cardElement) {
        throw new Error("Card details are not available.");
      }

      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        throw new Error(error.message || "Card setup failed.");
      }
      if (!setupIntent || setupIntent.status !== "succeeded") {
        throw new Error(`Card setup did not complete (status: ${setupIntent?.status ?? "unknown"}).`);
      }

      const req: ApiCreatePaymentMethodRequest = {
        prepared_payment_method: this.props.preparedPaymentMethod,
      };
      this.props.onCreate(req);
    } catch (e: any) {
      // Always cancel the save so the parent's "creating" spinner is cleared, and surface
      // the failure to the user instead of leaving an unhandled promise rejection.
      this.props.onSaveCanceled();
      this.props.onCardChange(false, e?.message || "Failed to add card.");
    }
  };

  render() {
    const cardStyle = {
      style: {
        base: {
          color: "#32325d",
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
        },
      },
    };

    return (
      <Grid width="16">
        <Grid.Row>
          <Grid.Column width="16">
            <Form.Field>
              <label>Card details</label>
              <div
                style={{
                  border: "1px solid rgba(34,36,38,.15)",
                  borderRadius: "4px",
                  padding: "11px 14px",
                }}
              >
                <CardElement
                  options={cardStyle}
                  onChange={(e: { complete: boolean; error?: { message: string } }) => {
                    this.props.onCardChange(e.complete, e.error?.message);
                  }}
                />
              </div>
            </Form.Field>
          </Grid.Column>
        </Grid.Row>
        {!!this.props.errorMessage && (
          <Grid.Row>
            <Grid.Column width="16">
              <Message content={this.props.errorMessage} error />
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    );
  }
}

class StripeCreditCard extends Component<IStripeCreditCardProps, IStripeCreditCardState> {
  private formRef?: { onClickSave: () => Promise<void> };

  state = {
    errorMessage: undefined,
    cardComplete: false,
    preparedPaymentMethod: this.props.preparedPaymentMethod,
    stripePromise: null,
  } as IStripeCreditCardState;

  static getDerivedStateFromProps(props: IStripeCreditCardProps, state: IStripeCreditCardState) {
    const ppm = props.preparedPaymentMethod;
    if (ppmIdentityChanged(ppm, state.preparedPaymentMethod)) {
      // script_url holds the Stripe publishable key.
      const publishableKey = ppm.script_url || "";
      return {
        preparedPaymentMethod: ppm,
        stripePromise: publishableKey ? loadStripe(publishableKey) : null,
        cardComplete: false,
        errorMessage: undefined,
      };
    }
    return null;
  }

  componentDidMount() {
    const publishableKey = this.props.preparedPaymentMethod.script_url || "";
    if (publishableKey) {
      this.setState({ stripePromise: loadStripe(publishableKey) });
    }
    this.props.onCanCreate(false);
  }

  componentDidUpdate(prevProps: IStripeCreditCardProps) {
    // getDerivedStateFromProps resets the form (cardComplete → false) when the prepared
    // payment method changes, but it is pure and cannot notify the parent. Propagate the
    // reset here so the parent's Save button is disabled again until the fresh CardElement
    // reports a complete card.
    if (ppmIdentityChanged(this.props.preparedPaymentMethod, prevProps.preparedPaymentMethod)) {
      this.props.onCanCreate(false);
    }
  }

  onClickSave = () => {
    if (!this.formRef) {
      // Don't throw: the parent invokes this via a setState callback, so cancel the save
      // (clear the parent spinner) and surface the error instead.
      this.props.onSaveCanceled();
      this.setState({ errorMessage: "Stripe form is not ready yet, please try again." });
      return;
    }
    // The inner save handles its own errors, but guard against any unexpected rejection so
    // the parent's "creating" spinner is never left stuck.
    this.formRef.onClickSave().catch((e: any) => {
      this.props.onSaveCanceled();
      this.setState({ errorMessage: e?.message || "Failed to add card." });
    });
  };

  onCardChange = (complete: boolean, errorMessage?: string) => {
    this.setState({ cardComplete: complete, errorMessage }, () => {
      this.props.onCanCreate(complete && !errorMessage);
    });
  };

  render() {
    const clientSecret = this.state.preparedPaymentMethod.token || "";
    // script_url holds the Stripe publishable key
    const publishableKey = this.state.preparedPaymentMethod.script_url || "";

    if (!publishableKey) {
      return <Message content="Stripe configuration missing (no publishable key)" warning />;
    }

    if (!this.state.stripePromise) {
      // Key is present but Stripe is still initializing (loadStripe runs in componentDidMount).
      return <Message content="Loading payment form…" info />;
    }

    return (
      <Elements stripe={this.state.stripePromise}>
        <ElementsConsumer>
          {({ stripe, elements }: { stripe: Stripe | null; elements: StripeElements | null }) => (
            <StripeCardForm
              stripe={stripe}
              elements={elements}
              clientSecret={clientSecret}
              preparedPaymentMethod={this.state.preparedPaymentMethod}
              onCanCreate={this.props.onCanCreate}
              onSaveCanceled={this.props.onSaveCanceled}
              onCreate={this.props.onCreate}
              errorMessage={this.state.errorMessage}
              onCardChange={this.onCardChange}
              innerRef={(ref) => {
                this.formRef = ref;
              }}
            />
          )}
        </ElementsConsumer>
      </Elements>
    );
  }
}

export default StripeCreditCard;
